import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import StatCard from "./ui/StatCard";
import ActivityChart from "./ui/ActivityChart";
import MiniChart from "./ui/MiniChart";
import RecentlyEditedSongs from "./ui/RecentlyEditedSongs";
import RecentlyEditedReports from "./ui/RecentlyEditedReports";
import SyncStatus from "./ui/SyncStatus";
import { toast } from "sonner";
import { Music, Clock, Calendar, Edit, FileText, TrendingUp } from "lucide-react";
import CircleChart from "./ui/CircleChart";
import ReportStatusChart from "./ui/ReportStatusChart";

const MainDashboard = () => {
  const { t, i18n } = useTranslation();
  const [activityFilter, setActivityFilter] = useState("Month");
  const [songCount, setSongCount] = useState(0);
  const [songsAddedThisMonth, setSongsAddedThisMonth] = useState(0);
  const [knowledgeGain, setKnowledgeGain] = useState("0%");
  const [lastSync, setLastSync] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [editedSongsCount, setEditedSongsCount] = useState(0);
  const [editedReportsCount, setEditedReportsCount] = useState(0);
  const [lastThreeMonths, setLastThreeMonths] = useState([]);
  const isRTL = i18n.dir() === "rtl";

  // Fetch song count, songs added this month, and other data
  useEffect(() => {
    const fetchSongCount = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongsCount`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setSongCount(data.count || 0);
      } catch (error) {
        console.error("Error fetching song count:", error);
        toast.error(t("admin.errorFetchingSongCount"));
      }
    };

    const fetchSongsAddedThisMonth = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/sync/songs-added-this-month`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setSongsAddedThisMonth(data.totalSongsAdded || 0);
      } catch (error) {
        console.error("Error fetching songs added this month:", error);
        toast.error(t("admin.errorFetchingSongsAdded"));
        setSongsAddedThisMonth(0);
      }
    };

    const fetchLastSync = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/sync/last-sync`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data && (data.data || data)) {
          const syncData = data.data || data;
          if (Array.isArray(syncData) && syncData.length > 0) {
            const sortedSyncs = [...syncData].sort(
              (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
            );
            setLastSync(sortedSyncs[0]);
          } else {
            setLastSync(syncData);
          }
        }
      } catch (error) {
        console.error("Error fetching last sync info:", error);
        toast.error(t("admin.errorFetchingLastSync"));
      }
    };

    const fetchEditedSongs = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/song-edits/count`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setEditedSongsCount(data.count || 0);
      } catch (error) {
        console.error("Error fetching edited songs count:", error.message);
        toast.error(t("admin.errorFetchingEditedSongs"));
        setEditedSongsCount(0);
      }
    };

    const fetchEditedReports = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/reports`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setEditedReportsCount(data.reports?.length || 0);
      } catch (error) {
        console.error("Error fetching edited reports count:", error);
        toast.error(t("admin.errorFetchingEditedReports"));
        setEditedReportsCount(0);
      }
    };

    const fetchSongsAddedLastYear = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/sync/songs-added-this-month`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data && Array.isArray(data.data)) {
          // Get last 3 months (handle wrap-around for Jan/Feb)
          const now = new Date();
          const months = data.data;
          const last3 = [];
          for (let i = 2; i >= 0; i--) {
            let monthIdx = (now.getMonth() - i + 12) % 12;
            const monthData = months.find(m => m.month === monthIdx + 1);
            last3.push({
              name: monthData?.name || '',
              value: monthData?.totalSongsAdded || 0,
              month: monthIdx + 1,
            });
          }
          setLastThreeMonths(last3);
        }
      } catch (error) {
        setLastThreeMonths([]);
      }
    };

    fetchSongCount();
    fetchSongsAddedThisMonth();
    fetchLastSync();
    fetchEditedSongs();
    fetchEditedReports();
    fetchSongsAddedLastYear();
  }, [t]);

  // Calculate percentage of songs added this month
  useEffect(() => {
    if (songCount > 0 && songsAddedThisMonth >= 0) {
      const percentage = ((songsAddedThisMonth / songCount) * 100).toFixed(1);
      setKnowledgeGain(`+${percentage}%`);
    } else {
      setKnowledgeGain("+0%");
    }
  }, [songCount, songsAddedThisMonth]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return t("admin.never");
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  // Calculate next scheduled sync time (Sunday at 2:00 AM)
  const getNextScheduledSync = () => {
    const now = new Date();
    const nextSync = new Date(now);
    nextSync.setDate(now.getDate() + ((7 - now.getDay()) % 7));
    nextSync.setHours(2, 0, 0, 0);

    if (now.getDay() === 0 && now.getHours() < 2) {
      nextSync.setDate(now.getDate());
    }

    if (nextSync <= now) {
      nextSync.setDate(nextSync.getDate() + 7);
    }

    return formatDate(nextSync);
  };

  const handleManualSync = async () => {
    try {
      setIsLoading(true);
      setProgress("");
      toast.success(t("admin.syncStarted"));

      const eventSource = new EventSource(
        `${process.env.REACT_APP_API_BASE_URL}/api/sync/sync`
      );

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.message) {
          setProgress((prev) => prev + "\n" + data.message);
        }
        if (data.error) {
          setProgress((prev) => prev + "\n❌ " + data.error);
        }

        if (data.message && data.message.includes("completed successfully")) {
          // Keep loading state active for a moment to show completion message
          setTimeout(() => {
            eventSource.close();
            setIsLoading(false);
          }, 3000); // Match the timeout in SyncStatus component

          // Refresh last sync info
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sync/last-sync`)
            .then((response) => response.json())
            .then((data) => {
              if (data && (data.data || data)) {
                const syncData = data.data || data;
                if (Array.isArray(syncData) && syncData.length > 0) {
                  const sortedSyncs = [...syncData].sort(
                    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
                  );
                  setLastSync(sortedSyncs[0]);
                } else {
                  setLastSync(syncData);
                }
              }
            })
            .catch((error) =>
              console.error("Error fetching updated sync info:", error)
            );

          // Refresh songs added this month
          fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/sync/songs-added-this-month`
          )
            .then((response) => response.json())
            .then((data) => {
              setSongsAddedThisMonth(data.totalSongsAdded || 0);
            })
            .catch((error) =>
              console.error("Error fetching updated songs added:", error)
            );

          // Refresh total song count
          fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/ArbSongs/getArbSongsCount`
          )
            .then((response) => response.json())
            .then((data) => {
              setSongCount(data.count || 0);
            })
            .catch((error) =>
              console.error("Error fetching updated song count:", error)
            );
        }
      };

      eventSource.onerror = () => {
        eventSource.close();
        setIsLoading(false);
        toast.error(t("admin.syncError"));
      };
    } catch (error) {
      console.error("Sync error:", error);
      setProgress((prev) => prev + "\n❌ Error: " + error.message);
      setIsLoading(false);
      toast.error(t("admin.syncFailed"));
    }
  };

  return (
    <div className={`flex-1 overflow-auto bg-white ${isRTL ? "rtl" : "ltr"}`}>
      <main className="mx-auto py-4 md:py-6 px-2 sm:px-4 md:px-8">
        <div className="flex flex-col gap-4">
          {/* Top: StatCards grid and ReportStatusChart */}
          <div className="flex flex-col lg:flex-row gap-4" style={{ minHeight: 260 }}>
            {/* StatCards grid */}
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 grid-rows-2 gap-4 flex-1"
            >
              <StatCard
                title={t("admin.totalSongs")}
                value={songCount.toString()}
                subtitle={t("admin.songs")}
                icon={<Music className="w-7 h-7 text-gray-400" />}
              />
              <StatCard
                title={t("admin.lastSync")}
                value={lastSync ? formatDate(lastSync.timestamp) : t("admin.never")}
                icon={<Clock className="w-7 h-7 text-gray-400" />}
              />
              <StatCard
                title={t("admin.nextScheduledSync")}
                value={getNextScheduledSync()}
                icon={<Calendar className="w-7 h-7 text-gray-400" />}
              />
              <StatCard
                title={t("admin.editedSongs")}
                value={editedSongsCount.toString()}
                subtitle={t("admin.songs")}
                icon={<Edit className="w-7 h-7 text-gray-400" />}
              />
              <StatCard
                title={t("admin.editedReports")}
                value={editedReportsCount.toString()}
                subtitle={t("admin.reports")}
                icon={<FileText className="w-7 h-7 text-gray-400" />}
              />
              <StatCard
                title={t("admin.persangeThisMonth")}
                value={knowledgeGain}
                chart={<MiniChart />}
                icon={<TrendingUp className="w-7 h-7 text-orange-500" />}
              />
            </div>
            {/* ReportStatusChart spans both rows */}
            <div
              className="flex-1 flex flex-col mt-4 lg:mt-0"
              style={{ minWidth: 0, height: "100%" }}
            >
              <ReportStatusChart style={{ height: "100%" }} />
            </div>
          </div>
          {/* Bottom: ActivityChart and CircleChart */}
          <div className="flex flex-col lg:flex-row gap-4">
            <div
              className="flex-1"
            >
              <ActivityChart
                title={t("admin.activity")}
                filter={activityFilter}
                onFilterChange={setActivityFilter}
              />
            </div>
            <div
              className="flex-1 flex items-center justify-center mt-4 lg:mt-0"
              style={{ minWidth: 0 }}
            >
              <CircleChart lastThreeMonths={lastThreeMonths} />
            </div>
          </div>
        </div>
        
        {/* Recently Edited Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 mt-8">
          <div className="border border-gray-100 rounded-2xl bg-white p-4 h-full">
            <RecentlyEditedSongs />
          </div>
          <div className="border border-gray-100 rounded-2xl bg-white p-4 h-full">
            <RecentlyEditedReports />
          </div>
        </div>
        <div className="mb-6">
          <SyncStatus
            lastSyncTime={
              lastSync ? formatDate(lastSync.timestamp) : t("admin.never")
            }
            songsAdded={lastSync ? lastSync.songsAdded : 0}
            nextScheduledSync={getNextScheduledSync()}
            syncType={lastSync ? lastSync.syncType : t("admin.notApplicable")}
            status={lastSync ? lastSync.status : t("admin.notApplicable")}
            onManualSync={handleManualSync}
            isLoading={isLoading}
            progress={progress}
          />
        </div>
      </main>
    </div>
  );
};

export default MainDashboard;
